import React, { useState, useEffect } from 'react'
import { Button, Form, Alert, Row, Col } from 'react-bootstrap'
import BigSpinner from 'common/components/BigSpinner'
import { Formik } from 'formik'
import { DataTable, ColumnDefinition } from 'common/components/DataTable'
import * as yup from 'yup'
import API from 'common/logic/API'
import { Navigate } from 'react-router-dom'
import { Fund } from 'types'

type ToInsert = { name: string }
type Duplicate = { name: string; existingAssetName: string; existingFundName: string }

type ParseResult = {
    fund: Fund
    toInsert: ToInsert[]
    duplicates: Duplicate[]
}

const ProcessState = Object.freeze({
    "LOADING": 1,
    "WAITING_INPUT": 2,
    "PARSING": 3,
    "WAITING_REVIEW": 4,
    "EXECUTING": 5,
    "DONE": 6,
    "ERROR": 7,
})

interface ReviewFormProps {
    parsed: ParseResult
    onExecuting: () => void
    onDone: () => void
    onError: (msg: string) => void
}

function ReviewForm({ parsed, onExecuting, onDone, onError }: ReviewFormProps) {
    const fundId = parsed.fund.id

    const schema = yup.object().shape({
        import: yup.array(),
    })

    const initialValues = {
        import: parsed.toInsert.map(() => true),
    }

    const submit = (values: any) => {
        onExecuting()
        const payload = parsed.toInsert
            .map((entry: ToInsert) => ({
                name: entry.name,
                fundId,
                amountHeld: '0',
                notes: null,
            }))
            .filter((_t: any, i: number) => values.import[i])

        API.post("/financial-assets/batch", payload)
            .then(() => onDone())
            .catch((e: any) => onError(e?.response?.data?.message ?? e.message))
    }

    const insertDefinitions = (handleChange: any, values: any) => [
        new ColumnDefinition("Import", (_obj: any, i: number) => (
            <Form.Check
                type="checkbox"
                label=""
                name={`import[${i}]`}
                checked={values.import[i]}
                onChange={handleChange}
            />
        )),
        new ColumnDefinition("Name", "name"),
    ]

    const duplicateDefinitions = [
        new ColumnDefinition("Skipped name", "name"),
        new ColumnDefinition("Existing asset", "existingAssetName"),
        new ColumnDefinition("Existing fund", "existingFundName"),
    ]

    return <>
        <Alert variant="success">
            Will insert <strong>{parsed.toInsert.length}</strong> new asset{parsed.toInsert.length === 1 ? '' : 's'} into fund <strong>{parsed.fund.name}</strong>.
        </Alert>
        <Formik
            validationSchema={schema}
            onSubmit={submit}
            initialValues={initialValues}
        >
            {({ handleSubmit, handleChange, values }) => (
                <Form noValidate onSubmit={handleSubmit}>
                    <DataTable
                        columns={insertDefinitions(handleChange, values)}
                        rows={parsed.toInsert}
                    />
                    <Button variant="primary" type="submit">Insert {parsed.toInsert.length} asset{parsed.toInsert.length === 1 ? '' : 's'}</Button>
                </Form>
            )}
        </Formik>

        {parsed.duplicates.length > 0 && (
            <>
                <h3 className="h5 mt-4">Skipped — already existing ({parsed.duplicates.length})</h3>
                <p className="text-muted">
                    These names already exist in your assets (case-insensitive, across all funds) and will not be inserted.
                </p>
                <DataTable
                    columns={duplicateDefinitions}
                    rows={parsed.duplicates}
                />
            </>
        )}
    </>
}

function ImportAssetNames() {
    const [step, setStep] = useState<number>(ProcessState.LOADING)
    const [funds, setFunds] = useState<Fund[]>([])
    const [error, setError] = useState<React.ReactNode | null>(null)
    const [parsed, setParsed] = useState<ParseResult | null>(null)
    const [lastText, setLastText] = useState('')
    const [lastFundId, setLastFundId] = useState<number | ''>('')

    useEffect(() => {
        API.get('/funds').then((r: any) => {
            const all = (r.data?.results ?? []) as Fund[]
            setFunds(all.filter(f => f.isInvestment && !f.deleted))
            setStep(ProcessState.WAITING_INPUT)
        }).catch((e: any) => {
            setStep(ProcessState.ERROR)
            setError(e.message)
        })
    }, [])

    const parseForm = (values: any) => {
        setError(null)
        setLastText(values.text)
        setLastFundId(Number(values.fundId))
        setStep(ProcessState.PARSING)
        const names = (values.text as string)
            .split('\n')
            .map(l => l.trim())
            .filter(l => l.length > 0)
        API.post("/financial-assets/import-preview", { fundId: Number(values.fundId), names })
            .then((response: any) => {
                setParsed(response.data)
                setStep(ProcessState.WAITING_REVIEW)
            }).catch((e: any) => {
                const label = e?.response?.data?.label
                const message = e?.response?.data?.message ?? e.message
                if (label === "invalid-parameter" || label === "invalid-reference") {
                    setStep(ProcessState.WAITING_INPUT)
                    setError(message)
                    setParsed(null)
                } else {
                    setStep(ProcessState.ERROR)
                    setError(message)
                }
            })
    }

    const formEl = () => {
        const schema = yup.object().shape({
            fundId: yup.number().required('Please select a fund'),
            text: yup.string().required('Please enter at least one asset name'),
        })

        return <>
            <Alert variant="info">
                Paste a list of asset names, one per line. Names that already exist
                (case-insensitive, across all your funds) will be skipped.
            </Alert>
            <Formik
                onSubmit={parseForm}
                initialValues={{ text: lastText, fundId: lastFundId || (funds[0]?.id ?? '') }}
                validationSchema={schema}
                enableReinitialize
            >
                {({ handleSubmit, handleChange, values, errors }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                        <Form.Group as={Row} controlId="fundControl" className="mb-3">
                            <Form.Label column sm="2">Fund</Form.Label>
                            <Col>
                                <Form.Control
                                    as="select"
                                    name="fundId"
                                    value={values.fundId}
                                    onChange={handleChange}
                                    isInvalid={!!errors.fundId}
                                >
                                    {funds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                                </Form.Control>
                                <Form.Control.Feedback type="invalid">{errors.fundId as string}</Form.Control.Feedback>
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} controlId="textControl" className="mb-3">
                            <Form.Label column sm="2">Asset names</Form.Label>
                            <Col>
                                <Form.Control
                                    as="textarea"
                                    rows={10}
                                    name="text"
                                    value={values.text}
                                    onChange={handleChange}
                                    isInvalid={!!errors.text}
                                    placeholder={'FTSE All-World (Acc)\nNVIDIA\nEli Lilly\nEvotec'}
                                />
                                <Form.Control.Feedback type="invalid">{errors.text as string}</Form.Control.Feedback>
                            </Col>
                        </Form.Group>
                        <Button variant="primary" type="submit">Parse</Button>
                    </Form>
                )}
            </Formik>
        </>
    }

    const reviewForm = () => {
        if (!parsed) return null
        if (parsed.toInsert.length === 0) {
            return <Alert variant="warning">No new assets to insert — every name is a duplicate.</Alert>
        }
        return <ReviewForm
            parsed={parsed}
            onExecuting={() => setStep(ProcessState.EXECUTING)}
            onDone={() => setStep(ProcessState.DONE)}
            onError={(msg: string) => {
                setStep(ProcessState.ERROR)
                setError(msg)
            }}
        />
    }

    const renderBody = () => {
        switch (step) {
            case ProcessState.LOADING:
                return <BigSpinner />
            case ProcessState.WAITING_INPUT:
            case ProcessState.WAITING_REVIEW:
                return <>
                    {formEl()}
                    <hr />
                    {step === ProcessState.WAITING_REVIEW && reviewForm()}
                </>
            case ProcessState.PARSING:
                return <BigSpinner />
            case ProcessState.EXECUTING:
                return <BigSpinner />
            case ProcessState.ERROR:
                return <></>
            case ProcessState.DONE:
                return <Navigate to="/investments/assets" replace />
            default:
                return <></>
        }
    }

    return <>
        <h1>Import assets</h1>
        {error ? <Alert variant="danger">{error}</Alert> : null}
        {renderBody()}
    </>
}

export default ImportAssetNames